
import { useParams, Link } from 'react-router-dom';
import { AppNavbar } from '@/components/layout/AppNavbar';
import { useProduct } from '@/hooks/useProduct';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ExternalLink, MapPin } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChemicalSafetyScorecard } from '@/components/directory/ChemicalSafetyScorecard';

const ProductDetailDirectory = () => {
  const { productId } = useParams<{ productId: string }>();
  const { product, isLoading, error } = useProduct(productId || '');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <AppNavbar />
        <main className="container py-12">
          <div className="flex justify-center items-center h-64">
            <p>Loading product details...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background">
        <AppNavbar />
        <main className="container py-12">
          <div className="bg-destructive/10 p-4 rounded-md text-destructive mb-6">
            Product not found.
          </div>
          <Link to="/directory">
            <Button variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Directory
            </Button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppNavbar />
      
      <main className="container py-12">
        {/* Back Button */}
        <div className="mb-6">
          <Link to="/directory">
            <Button variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Directory
            </Button>
          </Link>
        </div>

        {/* Product Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
              {product.manufacturer && (
                <p className="text-lg text-muted-foreground mb-2">{product.manufacturer}</p>
              )}
              <Badge variant="default" className="bg-green-100 text-green-800">
                ✓ Certified Product
              </Badge>
            </div>
          </div>
          
          {product.description && (
            <p className="text-muted-foreground max-w-3xl">{product.description}</p>
          )}
        </div>

        {/* Product Information */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Product Details */}
            <Card>
              <CardHeader>
                <CardTitle>Product Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-1">Product Type</h4>
                    <p className="capitalize">{product.product_type?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-1">SKU</h4>
                    <p>SKU-{product.id.slice(0, 8).toUpperCase()}</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-1">Format</h4>
                    <p>Liquid Spray</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-1">Packaging</h4>
                    <p>32 oz Recyclable Bottle</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Chemical Safety Scorecard */}
            <ChemicalSafetyScorecard product={product} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Where to Buy */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Where to Buy
                </CardTitle>
                <CardDescription>Find this certified product at these retailers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Green Supply Co.</h4>
                        <p className="text-sm text-muted-foreground">Online & 15 stores</p>
                      </div>
                      <Button size="sm" variant="outline">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Visit
                      </Button>
                    </div>
                  </div>
                  
                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">EcoMart</h4>
                        <p className="text-sm text-muted-foreground">Online store</p>
                      </div>
                      <Button size="sm" variant="outline">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Visit
                      </Button>
                    </div>
                  </div>

                  <div className="border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Sustainable Solutions</h4>
                        <p className="text-sm text-muted-foreground">B2B supplier</p>
                      </div>
                      <Button size="sm" variant="outline">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Contact
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="text-xs text-muted-foreground">
                    Retailer information provided by manufacturer. Availability may vary.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Certification Info */}
            <Card>
              <CardHeader>
                <CardTitle>Certification</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center p-4">
                  <img 
                    src="/lovable-uploads/db5c9f04-b178-4abc-b8ed-9e09d1e7c292.png" 
                    alt="Green Seal Certified" 
                    className="h-16 w-auto" 
                  />
                </div>
                <div className="text-center">
                  <p className="font-medium mb-1">Green Seal Certified</p>
                  <p className="text-sm text-muted-foreground">
                    This product meets strict environmental and health standards.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProductDetailDirectory;
